/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState } from "react";
import { applyTheme, defaultTheme, getAntdTheme, type ThemeConfig } from "./index";
import { ConfigProvider, theme as antdTheme } from "antd";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  themeConfig: ThemeConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 深色模式配置
const darkTheme: ThemeConfig = {
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    gray50: "#1e1e1e",
    gray100: "#333333",
    gray200: "#4a4a4a",
    gray300: "#666666",
    gray400: "#888888",
    gray500: "#aaaaaa",
    gray900: "#ffffff",
    blue50: "#111b2b",
    blue500: "#3b82f6",
    blue700: "#60a5fa",
  },
  colors: {
    primary: "#3b82f6",
    primaryLight: "#111b2b",
    primaryDark: "#60a5fa",
  },
  layout: {
    background: "#121212",
    siderBackground: "#1e1e1e",
    headerBackground: "#1e1e1e",
    tabsBackground: "#121212",
    siderShadow: "inset -1px 0px 0px 0px #333333",
  },
  menu: {
    ...defaultTheme.menu,
    itemColor: "#aaaaaa",
    itemIconColor: "#aaaaaa",
    selectedBg: "#111b2b",
    selectedColor: "#3b82f6",
    selectedIconColor: "#3b82f6",
    hoverBg: "#2a2a2a",
  },
  text: {
    primary: "#ffffff",
    secondary: "#aaaaaa",
    disabled: "rgba(255, 255, 255, 0.35)",
    logo: "#ffffff",
  },
  border: {
    ...defaultTheme.border,
    color: "#333333",
  },
  shadow: {
    small: "0 1px 2px rgba(0, 0, 0, 0.3)",
    medium: "0 4px 10px -4px rgba(0, 0, 0, 0.5)",
    large: "0 12px 30px -12px rgba(0, 0, 0, 0.7)",
  },
};

export const ThemeProvider: React.FC<{ children: React.ReactNode; initialTheme?: ThemeConfig }> = ({
  children,
  initialTheme,
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem("theme-mode") as ThemeMode) || "system";
  });

  const [activeTheme, setActiveTheme] = useState<ThemeConfig>(initialTheme ?? defaultTheme);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const handleThemeChange = () => {
      let currentIsDark = false;
      if (mode === "system") {
        currentIsDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      } else {
        currentIsDark = mode === "dark";
      }

      setIsDark(currentIsDark);
      const newTheme = currentIsDark ? darkTheme : initialTheme ?? defaultTheme;
      setActiveTheme(newTheme);
      applyTheme(newTheme);
      localStorage.setItem("theme-mode", mode);

      if (currentIsDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    handleThemeChange();

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => {
      if (mode === "system") handleThemeChange();
    };

    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }, [mode, initialTheme]);

  const antdThemeConfig = {
    ...getAntdTheme(activeTheme),
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  };

  return (
    <ThemeContext.Provider value={{ mode, setMode, themeConfig: activeTheme }}>
      <ConfigProvider theme={antdThemeConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
};
